import React from 'react';
import { categoryInfo } from '../data/quizData';
import { QuizQuestion } from '../types/quiz';
import { Explanation } from './Explanation';
import { MockWebsite } from './MockWebsite';
import { ProgressBar } from './ProgressBar';
import { QuizOption } from './QuizOption';

interface QuizProps {
  question: QuizQuestion;
  currentIndex: number;
  totalQuestions: number;
  progress: number;
  selectedOption: string | null;
  showExplanation: boolean;
  // eslint-disable-next-line no-unused-vars
  onAnswer: (optionId: string) => void;
  onNext: () => void;
}

export const Quiz: React.FC<QuizProps> = ({
  question,
  currentIndex,
  totalQuestions,
  progress,
  selectedOption,
  showExplanation,
  onAnswer,
  onNext,
}) => {
  const category = categoryInfo[question.category];
  const isCorrect = selectedOption
    ? question.options.find(opt => opt.id === selectedOption)?.isCorrect ?? false
    : false;

  // Refactorisation des ternaires imbriqués
  let difficultyStyle = {};
  let difficultyClass = 'px-3 py-1 rounded-full font-semibold border';
  let difficultyLabel = '';
  switch (question.difficulty) {
    case 'easy':
      difficultyStyle = {
        backgroundColor: 'var(--wcag-success-bg)',
        borderColor: 'var(--wcag-success-border)',
        color: 'var(--wcag-success-text)',
      };
      difficultyLabel = '🟢 Facile';
      break;
    case 'medium':
      difficultyStyle = {
        backgroundColor: '#fffbeb',
        borderColor: '#d97706',
        color: '#78350f',
      };
      difficultyLabel = '🟡 Moyen';
      break;
    default:
      difficultyStyle = {
        backgroundColor: 'var(--wcag-danger-bg)',
        borderColor: 'var(--wcag-danger-border)',
        color: 'var(--wcag-danger-text)',
      };
      difficultyLabel = '🔴 Difficile';
      break;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-7xl w-full">
        <ProgressBar
          progress={progress}
          currentQuestion={currentIndex + 1}
          totalQuestions={totalQuestions}
        />

        <div className={`grid gap-6 ${question.mockWebsite ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
          {/* Colonne de gauche : Fausse page web (si elle existe) */}
          {question.mockWebsite && (
            <div className="card animate-fade-in h-[700px] md:h-[800px] lg:h-[900px] lg:sticky lg:top-4">
              <h3
                className="text-sm font-semibold mb-3 flex items-center gap-2"
                style={{ color: 'var(--wcag-text)' }}
              >
                <span>🖥️</span>
                <span>Exemple visuel</span>
              </h3>
              <div className="h-[calc(100%-2rem)]">
                <MockWebsite mockWebsite={question.mockWebsite} />
              </div>
            </div>
          )}

          {/* Colonne de droite : Questions */}
          <div className="card animate-fade-in">
            {question.image && (
              <div className="mb-4 flex justify-center">
                <img
                  src={question.image}
                  alt="Illustration de l'arnaque"
                  className="rounded-lg shadow max-h-64"
                />
              </div>
            )}
            <div className="mb-6">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-linear-to-r ${category.color} text-white text-sm font-semibold mb-4 shadow-md`}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </div>

              <div className="flex items-center gap-2 text-sm mb-4">
                <span className={difficultyClass} style={difficultyStyle}>
                  {difficultyLabel}
                </span>
              </div>

              {question.context && (
                <div className="bg-primary-50 dark:bg-primary-950 p-4 rounded-lg mb-4 border-l-4 border-primary-500 dark:border-primary-400">
                  <p className="text-sm italic font-medium" style={{ color: 'var(--wcag-text)' }}>
                    📝 Contexte : {question.context}
                  </p>
                </div>
              )}

              <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--wcag-text)' }}>
                {question.question}
              </h2>
            </div>

            <div className="space-y-3">
              {question.options.map(option => (
                <QuizOption
                  key={option.id}
                  option={option}
                  isSelected={selectedOption === option.id}
                  showAnswer={showExplanation}
                  onSelect={onAnswer}
                />
              ))}
            </div>

            {showExplanation && (
              <>
                <Explanation question={question} isCorrect={isCorrect} />

                <div className="mt-6">
                  <button onClick={onNext} className="btn-primary w-full">
                    {currentIndex + 1 === totalQuestions
                      ? '🏁 Voir les résultats'
                      : '➡️ Question suivante'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
